import React, { useEffect, useState } from 'react';
import { SaveIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useContactData } from '../../lib/cmsStore';
import { ContactPageData } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Tabs } from '../../components/ui/Tabs';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function ContactEditor() {
  const {
    data,
    updateData,
    isLoading
  } = useContactData();
  const [localData, setLocalData] = useState<ContactPageData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && data && !localData) {
      setLocalData(data);
    }
  }, [data, isLoading, localData]);
  if (isLoading || !localData) {
    return <div className="p-8 font-mono text-sm text-muted-foreground" data-id="element-1153">
        Loading...
      </div>;
  }
  const handleSave = () => {
    setIsSaving(true);
    updateData(localData);
    setTimeout(() => setIsSaving(false), 500);
  };
  const updateField = (field: keyof ContactPageData, value: any) => {
    setLocalData(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const updateArrayItem = (field: keyof ContactPageData, index: number, key: string, value: any) => {
    setLocalData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] as any[])];
      arr[index] = {
        ...arr[index],
        [key]: value
      };
      return {
        ...prev,
        [field]: arr
      };
    });
  };
  const removeArrayItem = (field: keyof ContactPageData, index: number) => {
    setLocalData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] as any[])];
      arr.splice(index, 1);
      return {
        ...prev,
        [field]: arr
      };
    });
  };
  const addArrayItem = (field: keyof ContactPageData, emptyItem: any) => {
    setLocalData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: [...(prev[field] as any[]), emptyItem]
      };
    });
  };
  const tabs = [{
    id: 'content',
    label: 'Content',
    content: <div className="space-y-8 max-w-3xl py-6" data-id="element-1154">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1155">
            <Input label="Page Title" value={localData.pageTitle} onChange={e => updateField('pageTitle', e.target.value)} data-id="element-1156" />
            <Input label="Email Address" value={localData.email} onChange={e => updateField('email', e.target.value)} data-id="element-1157" />
            <Input label="Location" value={localData.location} onChange={e => updateField('location', e.target.value)} data-id="element-1158" />
          </div>
        </div>
  }, {
    id: 'social',
    label: 'Social Links',
    content: <div className="space-y-8 max-w-3xl py-6" data-id="element-1159">
          <div className="flex items-center justify-between border-b border-border pb-2" data-id="element-1160">
            <h3 className="font-display text-xl font-bold" data-id="element-1161">Social Links</h3>
            <Button size="sm" variant="outline" onClick={() => addArrayItem('socialLinks', {
          platform: '',
          url: '',
          visible: true
        })} data-id="element-1162">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1163" /> Add Link
            </Button>
          </div>
          <div className="space-y-3" data-id="element-1164">
            {localData.socialLinks.map((link, i) => <div key={i} className="flex items-center gap-3 bg-card border border-border p-3" data-id="element-1165">
                <Input value={link.platform} onChange={e => updateArrayItem('socialLinks', i, 'platform', e.target.value)} placeholder="Platform (e.g. Twitter)" className="w-1/3" data-id="element-1166" />
                <Input value={link.url} onChange={e => updateArrayItem('socialLinks', i, 'url', e.target.value)} placeholder="URL" className="flex-1" data-id="element-1167" />
                <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-1168">
                  <input type="checkbox" checked={link.visible} onChange={e => updateArrayItem('socialLinks', i, 'visible', e.target.checked)} className="accent-primary" data-id="element-1169" />
                  Show
                </label>
                <Button variant="ghost" size="sm" onClick={() => removeArrayItem('socialLinks', i)} className="text-destructive ml-2" data-id="element-1170">
                  <Trash2Icon className="w-4 h-4" data-id="element-1171" />
                </Button>
              </div>)}
          </div>
        </div>
  }];
  return <div className="flex flex-col h-full bg-background" data-id="element-1172">
      <header className="flex items-center justify-between px-8 py-4 border-b border-border bg-card sticky top-0 z-10" data-id="element-1173">
        <div data-id="element-1174">
          <h1 className="font-display text-xl font-bold tracking-tight" data-id="element-1175">
            Contact Page
          </h1>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1176">
            Manage contact info and social links.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1177">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1178" />
          <Button onClick={handleSave} disabled={isSaving} className="gap-2 min-w-[120px]" data-id="element-1179">
            <SaveIcon className="w-4 h-4" data-id="element-1180" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-hidden p-8 max-w-7xl mx-auto w-full" data-id="element-1181">
        <Tabs tabs={tabs} data-id="element-1182" />
      </main>
      <VersionHistoryPanel contentKey="sinka_contact" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      setLocalData(data);
      updateData(data);
    }} data-id="element-1183" />
    </div>;
}